import { Component, ElementRef, EventEmitter, forwardRef, inject, Injector, Input, Output, ViewChild } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { NgxMaskDirective } from 'ngx-mask';
import { MatDateFormats, MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DateAdapter } from '@angular/material/core';
import { LOCALE_ID, OnInit } from '@angular/core';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatTooltip} from '@angular/material/tooltip';

export const formatacao_data_br: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Component({
  selector: 'app-form-field',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    NgxMaskDirective,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckbox,
    MatTooltip,
  ],
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_DATE_FORMATS, useValue: formatacao_data_br },
    provideNativeDateAdapter(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormFieldComponent),
      multi: true
    }
  ],
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.css'
})
export class FormFieldComponent implements ControlValueAccessor, OnInit {
  @Input() classe = '';
  @Input() type = 'text';
  @Input() placeholder = 'Digite';
  @Input() dica?: string;
  @Input() value: any;
  @Input() mask?: string;
  @Input() textPrefix?: string;
  @Input() step: number | string = 'any';
  @Input() min = 0;
  @Input() max = 1e19;

  @Input() label?: string;
  @Input() colorLabel?: string;

  @Input() iconPrefix?: string;
  @Input() iconSuffix?: string;

  @Input() widthFormField = '100%';
  @Input() heightFormField = '60px';

  @Input() borderColor?: string;
  @Input() backgroundColor?: string;
  @Input() iconColor?: string;

  @Input() font?: string;
  @Input() fontLabel?: string;
  @Input() fontWeight?: string;

  @Input() isSenha = false;
  @Input() autocomplete?: string;
  @Input() iconSuffixSenha = 'visibility';

  @Input() isDateRange?: boolean = false;
  @Input() dataInicio?: Date = new Date();
  @Input() dataFim?: Date = new Date();

  @Input() maxLengthFormFiles?: number;
  @Input() disabled = false;

  @Input() showValidationErrors = true;
  @Input() customErrorMessages?: Record<string, string>;

  @Input() isCheckbox = false;
  @Input() isEdicao = false;

  @Input() carregandoInfo?: boolean = false;

  @Output() dataFimChange = new EventEmitter();
  @Output() dataInicioChange = new EventEmitter();

  @Output() changeValue = new EventEmitter();
  @Output() focusOutValue = new EventEmitter();

  @ViewChild('internalInput') internalInput!: ElementRef<HTMLInputElement>;

  private onChange: (value: any) => void = () => { /**/ };
  private onTouched = () => { /**/ };

  private _control: any;
  private firstChangeComMask = false;

  private readonly _adapter = inject<DateAdapter<any>>(DateAdapter);
  private readonly locale = inject(LOCALE_ID);
  private readonly injector = inject(Injector);

  ngOnInit(): void {
    this._adapter.setLocale('pt-BR');

    if (this.isSenha) {
      this.type = 'password';
    }

    try {
      this._control = this.injector.get(NgControl, null);
      if (this._control?.name && this.mask && this.isEdicao) {
        this.firstChangeComMask = true;
      }
    } catch {
      // nada a fazer
    }
  }

  focus() {
    setTimeout(() => {
      this.internalInput?.nativeElement?.focus();
    }, 0);
  }

  writeValue(value: any): void {
    if (this.isDateRange) {
      if (value && typeof value === 'object') {
        this.dataInicio = value.dataInicio || new Date();
        this.dataFim = value.dataFim || new Date();
      }
    } else {
      this.value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleMostrarSenha() {
    this.iconSuffixSenha = this.iconSuffixSenha === 'visibility' ? 'visibility_off' : 'visibility';
    this.type = this.type === 'password' ? 'text' : 'password';
  }

  change() {
    if (!this.isDateRange) {
      if (this.firstChangeComMask && this.isEdicao) {
        this.firstChangeComMask = !this.firstChangeComMask;
      } else {
        this.changeValue.emit(this.value);
        this.onChange(this.value);
      }
    } else {
      const dateRange = {
        dataInicio: this.dataInicio,
        dataFim: this.dataFim
      };
      this.dataInicioChange.emit(this.dataInicio);
      this.dataFimChange.emit(this.dataFim);
      this.onChange(dateRange);
    }
  }

  focusOut() {
    this.focusOutValue.emit(this.value);
    this.onTouched();
  }

  get isInvalid(): boolean {
    return this._control?.invalid && this._control?.dirty;
  }

  get errors(): any {
    return this._control?.errors;
  }

  getErrorMessage(): string {
    if (!this.errors) return '';

    if (this.customErrorMessages) {
      for (const errorKey of Object.keys(this.errors)) {
        if (this.customErrorMessages[errorKey]) {
          return this.customErrorMessages[errorKey];
        }
      }
    }

    if (this.errors['required']) {
      return `${this.label || 'Este campo'} é obrigatório`;
    }
    if (this.errors['email']) {
      return 'Digite um email válido';
    }
    if (this.errors['minlength']) {
      const requiredLength = this.errors['minlength'].requiredLength;
      return `Mínimo de ${requiredLength} caracteres`;
    }
    if (this.errors['maxlength']) {
      const requiredLength = this.errors['maxlength'].requiredLength;
      return `Máximo de ${requiredLength} caracteres`;
    }
    if (this.errors['min']) {
      return `Valor mínimo: ${this.errors['min'].min}`;
    }
    if (this.errors['max']) {
      return `Valor máximo: ${this.errors['max'].max}`;
    }
    if (this.errors['pattern']) {
      return 'Formato inválido';
    }
    if (this.errors['maxMenorQueMin']) {
      return 'Valor máximo deve ser maior que o mínimo';
    }
    if (this.errors['existsCpf']) {
      return 'CPF já cadastrado no sistema';
    }
    if (this.errors['existsEmail']) {
      return 'E-mail já cadastrado no sistema';
    }
    if (this.errors['cpf']) {
      return 'CPF inválido';
    }

    return 'Campo inválido';
  }

  changeCheckbox() {
    if (this.isCheckbox) {
      this.changeValue.emit(this.value);
    }
  }
}
